import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Avatar,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  FormControlLabel,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
} from '@mui/material';
import {
  Home,
  Person,
  Business,
  Security,
  Edit,
  Save,
  Cancel,
  PhotoCamera,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Email,
  Phone,
  LocationOn,
  Language,
  Shield,
  Key,
  Smartphone,
  History,
  ArrowBack,
} from '@mui/icons-material';
import { useAppSelector } from '../store/hooks';
import { useLocation } from 'react-router-dom';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const MyProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector(state => state.auth);
  const location = useLocation();

  // Check if we need to open Security tab from URL
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'security') return 2;
    return 0;
  };

  const [selectedTab, setSelectedTab] = useState(getInitialTab());

  // Personal Info State
  const [editing, setEditing] = useState(false);
  const [personalInfo, setPersonalInfo] = useState({
    name: user?.full_name || '',
    email: user?.email || '',
    phone: '',
    location: '',
    timezone: 'UTC-05:00 (Eastern Time)',
    language: 'English',
  });

  // Company Info State
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    companyName: 'Acme Corp',
    industry: 'Technology',
    website: 'https://acmecorp.com',
    address: '123 Main Street, Suite 100',
    city: 'New York',
    state: 'NY',
    zipCode: '10001',
    country: 'United States',
  });

  // Security State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState('');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handlePersonalInfoSave = async () => {
    try {
      // API call to update personal info
      // await api.patch('/users/me', personalInfo);
      setEditing(false);
      alert('Personal information updated successfully!');
    } catch (error) {
      console.error('Failed to update personal info:', error);
    }
  };

  const handleCompanyInfoSave = async () => {
    try {
      // API call to update company info
      // await api.patch('/users/me/company', companyInfo);
      setEditingCompany(false);
      alert('Company information updated successfully!');
    } catch (error) {
      console.error('Failed to update company info:', error);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordChangeError('');
    setPasswordChangeSuccess(false);

    if (newPassword !== confirmPassword) {
      setPasswordChangeError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordChangeError('Password must be at least 8 characters');
      return;
    }

    try {
      await api.post('/password/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });

      setPasswordChangeSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setPasswordChangeError(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const recentActivity = [
    { action: 'Password changed', date: '2025-11-15 14:30', device: 'Windows PC' },
    { action: 'Logged in', date: '2025-11-22 09:15', device: 'MacBook Pro' },
    { action: 'Profile updated', date: '2025-11-20 16:45', device: 'iPhone 13' },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard/settings')}
          sx={{ textTransform: 'none' }}
        >
          Back to Settings
        </Button>
      </Box>

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <MuiLink
          color="inherit"
          onClick={() => navigate('/dashboard')}
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', cursor: 'pointer' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </MuiLink>
        <Typography color="text.primary">My Profile</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom fontWeight="bold">
          My Profile
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Manage your personal information, company details, and security settings
        </Typography>
      </Box>

      {/* Profile Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  fontSize: '2.5rem',
                  bgcolor: '#1976d2',
                }}
              >
                {user?.full_name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
              <IconButton
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  bgcolor: 'white',
                  border: '2px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'grey.100' },
                }}
                size="small"
              >
                <PhotoCamera fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" fontWeight="bold">
                {user?.full_name || 'User Name'}
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {user?.email}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip label={user?.role || 'Agent'} color="primary" size="small" />
                <Chip label="Active" color="success" size="small" />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={handleTabChange} variant="fullWidth">
          <Tab icon={<Person />} iconPosition="start" label="Personal Info" />
          <Tab icon={<Business />} iconPosition="start" label="Company Details" />
          <Tab icon={<Security />} iconPosition="start" label="Security" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={selectedTab} index={0}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" fontWeight="bold">
                Personal Information
              </Typography>
              {!editing ? (
                <Button
                  startIcon={<Edit />}
                  variant="outlined"
                  onClick={() => setEditing(true)}
                >
                  Edit
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    startIcon={<Save />}
                    variant="contained"
                    onClick={handlePersonalInfoSave}
                  >
                    Save
                  </Button>
                  <Button
                    startIcon={<Cancel />}
                    variant="outlined"
                    onClick={() => setEditing(false)}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={personalInfo.name}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, name: e.target.value })}
                  disabled={!editing}
                  InputProps={{
                    startAdornment: <Person sx={{ color: 'action.active', mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  value={personalInfo.email}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, email: e.target.value })}
                  disabled={!editing}
                  InputProps={{
                    startAdornment: <Email sx={{ color: 'action.active', mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={personalInfo.phone}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, phone: e.target.value })}
                  disabled={!editing}
                  InputProps={{
                    startAdornment: <Phone sx={{ color: 'action.active', mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={personalInfo.location}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, location: e.target.value })}
                  disabled={!editing}
                  InputProps={{
                    startAdornment: <LocationOn sx={{ color: 'action.active', mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Timezone"
                  value={personalInfo.timezone}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, timezone: e.target.value })}
                  disabled={!editing}
                  select
                  SelectProps={{ native: true }}
                >
                  <option value="UTC-05:00 (Eastern Time)">UTC-05:00 (Eastern Time)</option>
                  <option value="UTC-06:00 (Central Time)">UTC-06:00 (Central Time)</option>
                  <option value="UTC-07:00 (Mountain Time)">UTC-07:00 (Mountain Time)</option>
                  <option value="UTC-08:00 (Pacific Time)">UTC-08:00 (Pacific Time)</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Language"
                  value={personalInfo.language}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, language: e.target.value })}
                  disabled={!editing}
                  InputProps={{
                    startAdornment: <Language sx={{ color: 'action.active', mr: 1 }} />,
                  }}
                  select
                  SelectProps={{ native: true }}
                >
                  <option value="English">English</option>
                  <option value="Spanish">Spanish</option>
                  <option value="French">French</option>
                  <option value="German">German</option>
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={selectedTab} index={1}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" fontWeight="bold">
                Company Details
              </Typography>
              {!editingCompany ? (
                <Button
                  startIcon={<Edit />}
                  variant="outlined"
                  onClick={() => setEditingCompany(true)}
                >
                  Edit
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    startIcon={<Save />}
                    variant="contained"
                    onClick={handleCompanyInfoSave}
                  >
                    Save
                  </Button>
                  <Button
                    startIcon={<Cancel />}
                    variant="outlined"
                    onClick={() => setEditingCompany(false)}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={companyInfo.companyName}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, companyName: e.target.value })}
                  disabled={!editingCompany}
                  InputProps={{
                    startAdornment: <Business sx={{ color: 'action.active', mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Industry"
                  value={companyInfo.industry}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, industry: e.target.value })}
                  disabled={!editingCompany}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Website"
                  value={companyInfo.website}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, website: e.target.value })}
                  disabled={!editingCompany}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={companyInfo.address}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, address: e.target.value })}
                  disabled={!editingCompany}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={companyInfo.city}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, city: e.target.value })}
                  disabled={!editingCompany}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="State"
                  value={companyInfo.state}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, state: e.target.value })}
                  disabled={!editingCompany}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Zip Code"
                  value={companyInfo.zipCode}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, zipCode: e.target.value })}
                  disabled={!editingCompany}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Country"
                  value={companyInfo.country}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, country: e.target.value })}
                  disabled={!editingCompany}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={selectedTab} index={2}>
        <Grid container spacing={3}>
          {/* Change Password */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Key />
                  Change Password
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {passwordChangeSuccess && (
                  <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircle />}>
                    Password changed successfully!
                  </Alert>
                )}

                {passwordChangeError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {passwordChangeError}
                  </Alert>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Current Password"
                      type={showPasswords ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPasswords(!showPasswords)}
                              edge="end"
                            >
                              {showPasswords ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="New Password"
                      type={showPasswords ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      helperText="Minimum 8 characters"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type={showPasswords ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      startIcon={<Key />}
                      onClick={handlePasswordChange}
                      disabled={!currentPassword || !newPassword || !confirmPassword}
                    >
                      Change Password
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Security Settings */}
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Shield />
                  Security Settings
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Smartphone />
                    </ListItemIcon>
                    <ListItemText
                      primary="Two-Factor Authentication"
                      secondary="Add an extra layer of security to your account"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={twoFactorEnabled}
                          onChange={(e) => setTwoFactorEnabled(e.target.checked)}
                        />
                      }
                      label=""
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon>
                      <Email />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email Notifications"
                      secondary="Receive security alerts and updates via email"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={emailNotifications}
                          onChange={(e) => setEmailNotifications(e.target.checked)}
                        />
                      }
                      label=""
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Activity */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <History />
                  Recent Activity
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <List dense>
                  {recentActivity.map((activity, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemText
                          primary={activity.action}
                          secondary={
                            <>
                              {activity.date}
                              <br />
                              {activity.device}
                            </>
                          }
                        />
                      </ListItem>
                      {index < recentActivity.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Security Tips
              </Typography>
              <Typography variant="body2">
                • Use a strong, unique password<br />
                • Enable two-factor authentication<br />
                • Review your account activity regularly<br />
                • Don't share your credentials
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </TabPanel>
    </Container>
  );
};

export default MyProfilePage;
